import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, X, RefreshCw, Send, AlertCircle, Calendar } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONVERSIONS = ['pending', 'yes', 'no', 'on_hold', 'not_converted'];
const COURIERS = ['BlueDart', 'Delhivery', 'DTDC', 'FedEx', 'Ecom Express', 'India Post', 'Other'];

const CONV_STYLE = {
  pending:       'bg-amber-900/40 text-amber-300 border-amber-700',
  yes:           'bg-green-900/40 text-green-300 border-green-700',
  no:            'bg-red-900/40 text-red-300 border-red-700',
  on_hold:       'bg-blue-900/40 text-blue-300 border-blue-700',
  not_converted: 'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const emptyForm = {
  sent_date: new Date().toISOString().slice(0, 10),
  client_name: '', client_contact: '', product_name: '',
  quantity_sent: '', tracking_id: '', courier: 'Delhivery',
  follow_up_date: '', feedback: '', conversion: 'pending',
  reason_not_converted: '', notes: '',
};

export default function Samples() {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [filterConv, setFilterConv] = useState('All');

  const fetchSamples = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/samples/`, { withCredentials: true });
      setSamples(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchSamples);

  useEffect(() => { fetchSamples(); }, []);

  const isOverdue = (s) => {
    if (!s.follow_up_date || s.conversion === 'yes' || s.conversion === 'no' || s.conversion === 'not_converted') return false;
    return new Date(s.follow_up_date) < new Date();
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      sent_date: s.sent_date, client_name: s.client_name, client_contact: s.client_contact || '',
      product_name: s.product_name, quantity_sent: s.quantity_sent || '',
      tracking_id: s.tracking_id || '', courier: s.courier || 'Delhivery',
      follow_up_date: s.follow_up_date || '', feedback: s.feedback || '',
      conversion: s.conversion, reason_not_converted: s.reason_not_converted || '',
      notes: s.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/samples/${editing.id}`, form, { withCredentials: true });
        setSamples(prev => prev.map(s => s.id === editing.id ? res.data : s));
      } else {
        const res = await axios.post(`${API}/samples/`, form, { withCredentials: true });
        setSamples(prev => [res.data, ...prev]);
      }
      setShowModal(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const deleteSample = (id, name) => {
    requestDelete(`/samples/${id}`, name || 'Sample', `Delete sample record "${name || 'this sample'}" permanently.`);
  };

  const quickUpdateConversion = async (id, conversion) => {
    try {
      const res = await axios.put(`${API}/samples/${id}`, { conversion }, { withCredentials: true });
      setSamples(prev => prev.map(s => s.id === id ? res.data : s));
    } catch (e) { toast.error('Error updating status'); }
  };

  const filtered = filterConv === 'All' ? samples : samples.filter(s => s.conversion === filterConv);
  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = samples.filter(s => s.follow_up_date && s.follow_up_date <= today && ['pending', 'on_hold'].includes(s.conversion)).length;
  const convertedCount = samples.filter(s => s.conversion === 'yes').length;
  const pendingCount = samples.filter(s => s.conversion === 'pending').length;

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading samples...</div>;

  return (
    <div className="space-y-5" data-testid="samples-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Sample Tracking</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{samples.length} samples · Dispatch & follow-up management</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchSamples} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800 transition-colors"><RefreshCw size={14} /></button>
          <button data-testid="add-sample-btn" onClick={openCreate} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> Add Sample
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Samples', value: samples.length, color: 'text-zinc-300' },
          { label: 'Converted', value: convertedCount, color: 'text-green-400' },
          { label: 'Pending Follow-up', value: pendingCount, color: 'text-amber-400' },
          { label: 'Overdue', value: overdueCount, color: 'text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className={`text-xl font-heading font-bold ${color}`}>{value}</div>
            <div className="text-zinc-500 text-xs mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Overdue Alert */}
      {overdueCount > 0 && (
        <div className="flex items-center gap-3 bg-red-900/20 border border-red-700/50 rounded-xl p-3" data-testid="overdue-alert">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-sm">{overdueCount} sample follow-up{overdueCount > 1 ? 's' : ''} overdue — check below</span>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['All', ...CONVERSIONS].map(c => (
          <button key={c} onClick={() => setFilterConv(c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${filterConv === c ? 'bg-amber-500 text-black border-amber-500' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}
          >{c === 'All' ? 'All' : c.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Sent Date', 'Client', 'Product', 'Qty', 'Tracking', 'Follow-up', 'Feedback', 'Conversion', ''].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const overdue = isOverdue(s);
              return (
                <tr key={s.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${overdue ? 'bg-red-950/10' : ''}`} data-testid={`sample-row-${s.id}`}>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{s.sent_date}</td>
                  <td className="px-4 py-3">
                    <div className="text-zinc-200 text-xs font-medium">{s.client_name}</div>
                    {s.client_contact && <div className="text-zinc-600 text-xs">{s.client_contact}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{s.product_name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{s.quantity_sent || '—'}</td>
                  <td className="px-4 py-3">
                    {s.tracking_id ? (
                      <div>
                        <div className="text-amber-400 font-mono text-xs">{s.tracking_id}</div>
                        {s.courier && <div className="text-zinc-600 text-xs">{s.courier}</div>}
                      </div>
                    ) : <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {s.follow_up_date ? (
                      <div className={`flex items-center gap-1 ${overdue ? 'text-red-400' : 'text-zinc-400'}`}>
                        {overdue && <AlertCircle size={11} />}
                        <span className="text-xs">{s.follow_up_date}</span>
                      </div>
                    ) : <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs max-w-[120px] truncate">{s.feedback || '—'}</td>
                  <td className="px-4 py-3">
                    <select
                      value={s.conversion}
                      onChange={e => quickUpdateConversion(s.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-lg border bg-transparent cursor-pointer ${CONV_STYLE[s.conversion] || ''}`}
                      data-testid={`conv-select-${s.id}`}
                    >
                      {CONVERSIONS.map(c => <option key={c} value={c} className="bg-zinc-900 text-zinc-50">{c.replace(/_/g, ' ')}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-sample-${s.id}`} onClick={() => openEdit(s)} className="text-zinc-500 hover:text-amber-400 transition-colors"><Edit2 size={13} /></button>
                      {isAdmin && <button data-testid={`delete-sample-${s.id}`} onClick={() => deleteSample(s.id, s.product_name)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center text-zinc-500 text-sm py-10">No samples found</div>}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h3 className="font-heading font-semibold text-zinc-50">{editing ? 'Edit Sample' : 'Add Sample Record'}</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="sample-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Sent Date</label>
                  <input type="date" value={form.sent_date} onChange={e => setForm(f => ({...f, sent_date: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Follow-up Date</label>
                  <input type="date" value={form.follow_up_date} onChange={e => setForm(f => ({...f, follow_up_date: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Client Name *</label>
                  <input required data-testid="sample-client-input" value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} placeholder="e.g. Haldiram's" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Contact</label>
                  <input value={form.client_contact} onChange={e => setForm(f => ({...f, client_contact: e.target.value}))} placeholder="+91 9800..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Product *</label>
                  <input required value={form.product_name} onChange={e => setForm(f => ({...f, product_name: e.target.value}))} placeholder="e.g. Peri Peri 100g" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Quantity Sent</label>
                  <input value={form.quantity_sent} onChange={e => setForm(f => ({...f, quantity_sent: e.target.value}))} placeholder="e.g. 10 kg" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Courier</label>
                  <select value={form.courier} onChange={e => setForm(f => ({...f, courier: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {COURIERS.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Tracking ID</label>
                  <input value={form.tracking_id} onChange={e => setForm(f => ({...f, tracking_id: e.target.value}))} placeholder="AWB / Tracking #" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Conversion</label>
                  <select value={form.conversion} onChange={e => setForm(f => ({...f, conversion: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {CONVERSIONS.map(c => <option key={c} value={c} className="bg-zinc-900">{c.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Feedback / Notes</label>
                  <textarea rows={2} value={form.feedback} onChange={e => setForm(f => ({...f, feedback: e.target.value}))} placeholder="Client feedback..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 resize-none" />
                </div>
                {(form.conversion === 'no' || form.conversion === 'not_converted') && (
                  <div className="col-span-2">
                    <label className="text-zinc-400 text-xs mb-1.5 block">Reason Not Converted</label>
                    <input value={form.reason_not_converted} onChange={e => setForm(f => ({...f, reason_not_converted: e.target.value}))} placeholder="e.g. Price too high" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button data-testid="submit-sample-btn" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : (editing ? 'Update Sample' : 'Add Sample')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}
